import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Container,
  Divider,
  Group,
  Pagination,
  Rating,
  Select,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import {
  IconBrandGoogle,
  IconCheck,
  IconMessage,
  IconMessagePlus,
  IconRefresh,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';
import { PermissionGate } from '../../components/PermissionGate';

function GoogleReviews() {
  const [page, setPage] = useState(1);
  const [starRating, setStarRating] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState<Record<string, string>>({});

  const utils = trpc.useUtils();
  const replyMutation = trpc.integrations.google.replyToReview.useMutation();
  const syncNowMutation = trpc.integrations.google.syncNow.useMutation();

  const status = trpc.integrations.google.status.useQuery();

  const reviewsQuery = trpc.integrations.google.listReviews.useQuery(
    {
      page,
      pageSize: 10,
      starRating: starRating ? Number(starRating) : undefined,
      unrepliedOnly: statusFilter === 'unreplied',
      googleLocationId: selectedLocationId ? Number(selectedLocationId) : undefined,
    },
    {
      enabled: !!status.data?.connected,
    },
  );

  const handleReplySubmit = async (reviewName: string) => {
    const comment = replyText[reviewName]?.trim();
    if (!comment) return;

    try {
      await replyMutation.mutateAsync({ reviewName, replyComment: comment });
      notifications.show({
        message: 'Reply posted successfully',
        color: 'green',
      });
      // Clear text
      setReplyText((prev) => {
        const next = { ...prev };
        delete next[reviewName];
        return next;
      });
      void reviewsQuery.refetch();
    } catch (err) {
      notifications.show({
        message: 'Failed to post reply',
        color: 'red',
      });
    }
  };

  const handleSyncNow = async () => {
    try {
      await syncNowMutation.mutateAsync();
      notifications.show({
        message: 'Reviews synchronized successfully',
        color: 'green',
      });
      void reviewsQuery.refetch();
    } catch (err) {
      notifications.show({
        message: 'Failed to sync reviews',
        color: 'red',
      });
    }
  };

  if (!status.data?.connected) {
    return (
      <Container size="sm" py={40}>
        <Card withBorder padding="xl" radius="md">
          <Stack align="center" gap="sm">
            <IconBrandGoogle size={48} color="#ea4335" />
            <Title order={3}>Google Account Not Connected</Title>
            <Text c="dimmed" size="sm" ta="center">
              Connect your Google Business Profile in the integrations settings to start viewing and
              replying to customer reviews directly from LeadSync.
            </Text>
            <Button color="red" mt="md" component="a" href="/vendor/integrations/google">
              Go to Integrations
            </Button>
          </Stack>
        </Card>
      </Container>
    );
  }

  const { items = [], totalCount = 0 } = reviewsQuery.data || {};
  const totalPages = Math.ceil(totalCount / 10);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Stack gap={2}>
            <Title order={2}>Google Business Profile Reviews</Title>
            <Text c="dimmed" size="sm">
              Read and reply to customer reviews across all your connected Google locations in
              real-time.
            </Text>
          </Stack>
          <Button
            variant="light"
            color="red"
            leftSection={<IconRefresh size={16} />}
            onClick={handleSyncNow}
            loading={syncNowMutation.isPending}
          >
            Sync Reviews
          </Button>
        </Group>

        {/* Filters */}
        <Card withBorder padding="md" radius="md">
          <Group gap="md">
            {status.data?.connected && status.data.locations.length > 0 && (
              <Select
                label="Filter by Location"
                placeholder="All Locations"
                data={status.data.locations.map((l) => ({
                  value: String(l.id),
                  label: l.displayName,
                }))}
                value={selectedLocationId}
                onChange={(val) => {
                  setSelectedLocationId(val);
                  setPage(1);
                }}
                clearable
                style={{ width: 220 }}
              />
            )}

            <Select
              label="Star Rating"
              placeholder="All Ratings"
              data={[
                { value: '5', label: '5 Stars' },
                { value: '4', label: '4 Stars' },
                { value: '3', label: '3 Stars' },
                { value: '2', label: '2 Stars' },
                { value: '1', label: '1 Star' },
              ]}
              value={starRating}
              onChange={(val) => {
                setStarRating(val);
                setPage(1);
              }}
              clearable
              style={{ width: 180 }}
            />

            <Select
              label="Reply Status"
              data={[
                { value: 'all', label: 'All Reviews' },
                { value: 'unreplied', label: 'Unreplied' },
              ]}
              value={statusFilter}
              onChange={(val) => {
                setStatusFilter(val || 'all');
                setPage(1);
              }}
              style={{ width: 180 }}
            />
          </Group>
        </Card>

        {/* Reviews List */}
        {reviewsQuery.isLoading ? (
          <Stack align="center" justify="center" py={40}>
            <IconRefresh size={32} className="animate-spin" />
            <Text size="sm" c="dimmed">
              Loading reviews...
            </Text>
          </Stack>
        ) : items.length === 0 ? (
          <Card withBorder padding="xl" radius="md">
            <Stack align="center" gap="xs">
              <IconMessage size={32} color="gray" />
              <Text fw={500}>No reviews found</Text>
              <Text size="xs" c="dimmed" ta="center">
                Try changing your filters or click Sync Reviews to fetch the latest.
              </Text>
            </Stack>
          </Card>
        ) : (
          <Stack gap="md">
            {items.map((review) => (
              <Card key={review.reviewName} withBorder padding="lg" radius="md">
                <Stack gap="md">
                  <Group justify="space-between" align="flex-start">
                    <Group gap="sm">
                      <Avatar
                        src={review.profilePhotoUrl}
                        alt={review.reviewerName}
                        radius="xl"
                        size="md"
                      >
                        {review.reviewerName[0]}
                      </Avatar>
                      <Stack gap={0}>
                        <Text fw={600} size="sm">
                          {review.reviewerName}
                        </Text>
                        <Text size="xs" c="dimmed">
                          Reviewed {new Date(review.reviewTime).toLocaleDateString()} •{' '}
                          {review.locationName}
                        </Text>
                      </Stack>
                    </Group>
                    <Rating value={review.starRating} readOnly size="sm" />
                  </Group>

                  {review.comment ? (
                    <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
                      {review.comment}
                    </Text>
                  ) : (
                    <Text size="sm" c="dimmed" fs="italic">
                      No review comment provided.
                    </Text>
                  )}

                  <Divider />

                  {/* Reply Section */}
                  {review.replyComment ? (
                    <Stack
                      gap="xs"
                      bg="var(--mantine-color-gray-0)"
                      p="sm"
                      style={{ borderRadius: '8px' }}
                    >
                      <Group justify="space-between">
                        <Group gap="xs">
                          <IconMessage size={16} color="var(--mantine-color-green-6)" />
                          <Text size="xs" fw={600} c="green">
                            Your Reply
                          </Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          {review.replyTime ? new Date(review.replyTime).toLocaleDateString() : ''}
                        </Text>
                      </Group>
                      <Text size="sm">{review.replyComment}</Text>

                      <Group justify="flex-end" mt="xs">
                        <Button
                          variant="subtle"
                          color="gray"
                          size="xs"
                          leftSection={<IconMessagePlus size={14} />}
                          onClick={() =>
                            setReplyText((prev) => ({
                              ...prev,
                              [review.reviewName]: review.replyComment || '',
                            }))
                          }
                        >
                          Edit Reply
                        </Button>
                      </Group>

                      {/* Edit Reply Input */}
                      {replyText[review.reviewName] !== undefined && (
                        <Stack gap="xs" mt="xs">
                          <Textarea
                            placeholder="Edit your reply..."
                            value={replyText[review.reviewName]}
                            onChange={(e) =>
                              setReplyText((prev) => ({
                                ...prev,
                                [review.reviewName]: e.target.value,
                              }))
                            }
                            rows={3}
                          />
                          <Group justify="flex-end" gap="xs">
                            <Button
                              variant="default"
                              size="xs"
                              onClick={() =>
                                setReplyText((prev) => {
                                  const next = { ...prev };
                                  delete next[review.reviewName];
                                  return next;
                                })
                              }
                            >
                              Cancel
                            </Button>
                            <Button
                              color="red"
                              size="xs"
                              leftSection={<IconCheck size={14} />}
                              onClick={() => handleReplySubmit(review.reviewName)}
                              loading={replyMutation.isPending}
                            >
                              Save Reply
                            </Button>
                          </Group>
                        </Stack>
                      )}
                    </Stack>
                  ) : (
                    <Stack gap="xs">
                      <Textarea
                        placeholder="Write a reply to this review..."
                        value={replyText[review.reviewName] || ''}
                        onChange={(e) =>
                          setReplyText((prev) => ({
                            ...prev,
                            [review.reviewName]: e.target.value,
                          }))
                        }
                        rows={3}
                      />
                      <Group justify="flex-end">
                        <Button
                          color="red"
                          size="xs"
                          leftSection={<IconMessagePlus size={14} />}
                          onClick={() => handleReplySubmit(review.reviewName)}
                          disabled={!replyText[review.reviewName]?.trim()}
                          loading={replyMutation.isPending}
                        >
                          Post Reply
                        </Button>
                      </Group>
                    </Stack>
                  )}
                </Stack>
              </Card>
            ))}

            {totalPages > 1 && (
              <Group justify="center" mt="lg">
                <Pagination total={totalPages} value={page} onChange={setPage} color="red" />
              </Group>
            )}
          </Stack>
        )}
      </Stack>
    </motion.div>
  );
}

function GoogleReviewsGated() {
  return (
    <PermissionGate permission="messaging">
      <GoogleReviews />
    </PermissionGate>
  );
}

export const Route = createFileRoute('/vendor/google/reviews')({
  component: GoogleReviewsGated,
});
